import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLang } from "../lib/useLang";
import { useCart } from "../state/CartContext";
import { useLocation } from "../state/LocationContext";
import { useAuth } from "../state/AuthContext";
import { round3 } from "../lib/pricing";
import { formatKWD } from "../lib/format";
import { deliveryEta } from "../lib/delivery";
import { placeOrder } from "../lib/orders";
import { addRecentOrder } from "../lib/recentOrders";
import { LocationSheet } from "../components/LocationSheet";
import { PromoField } from "../components/PromoField";
import { AddressFields, Field, addressSummary } from "../components/AddressFields";
import { ScheduleField } from "../components/ScheduleField";
import type { Address, PaymentMethod } from "../lib/types";

// COD-first launch: online methods appear only once payments are enabled.
const PAYMENTS_ENABLED = import.meta.env.VITE_PAYMENTS_ENABLED === "true";
const PAYMENT_METHODS: PaymentMethod[] = PAYMENTS_ENABLED
  ? ["knet", "card", "applepay", "cod"]
  : ["cod"];

export default function CheckoutPage() {
  const { t, lang, pick } = useLang();
  const { lines, subtotal, discount, discountedSubtotal, promoCode, clear } = useCart();
  const { branch, area, mode, pin, distanceKm, fulfillment, quote } = useLocation();
  const { user, addAddress, updateAddress } = useAuth();
  const navigate = useNavigate();
  const isPickup = fulfillment === "pickup";
  const savedAddresses = user?.addresses ?? [];

  const [name, setName] = useState(user?.name ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [addr, setAddr] = useState<Address>(
    user?.defaultAddress ?? {
      governorate: "",
      area: area ? (lang === "ar" ? area.ar : area.en) : "",
      block: "",
      street: "",
      building: "",
    },
  );
  const [payment, setPayment] = useState<PaymentMethod>(PAYMENT_METHODS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showLocation, setShowLocation] = useState(false);
  const [selectedAddrId, setSelectedAddrId] = useState<string | null>(null);
  const [saveAddr, setSaveAddr] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [scheduledFor, setScheduledFor] = useState<number | null>(null);

  const q = quote(subtotal);

  // Need a cart + a chosen branch to be here. If the location becomes
  // undeliverable the customer can fix it right here (below), so don't bounce them.
  if (!lines.length || !branch) {
    return <Navigate to="/cart" replace />;
  }

  const deliverable = Boolean(q?.deliverable && !q?.belowMin);
  const deliveryFee = q?.deliverable ? q.deliveryFee : 0;
  const total = round3(discountedSubtotal + deliveryFee);
  const eta = deliveryEta(lines);
  const locLabel =
    mode === "area" && area
      ? pick(area.en, area.ar)
      : pick(branch.name, branch.nameAr);

  async function submit() {
    if (!deliverable) {
      setShowLocation(true);
      return;
    }
    if (
      !name.trim() ||
      !phone.trim() ||
      (!isPickup &&
        (!addr.area || !addr.block || !addr.street || !addr.building))
    ) {
      setError(t("checkout.required"));
      return;
    }
    // A scheduled slot that has since passed would be silently downgraded to
    // ASAP by the server — reject it here so the customer re-picks a time.
    if (scheduledFor != null && scheduledFor <= Date.now()) {
      setError(t("schedule.slotPassed"));
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const result = await placeOrder({
        branchId: branch!.id,
        fulfillmentType: fulfillment,
        scheduledFor,
        customerUid: user?.uid ?? null,
        customer: { name: name.trim(), phone: phone.trim() },
        address: addr,
        geo: pin,
        distanceKm: distanceKm ?? -1,
        deliveryZone: mode === "area" && area ? { band: area.band, area: area.en } : null,
        deliveryFee,
        discount,
        promoCode: promoCode ?? undefined,
        lines,
        paymentMethod: payment,
      });

      // Order placed — now persist the address to the member's book, exactly
      // once. Update the picked address in place (so edits correct it rather
      // than duplicating); otherwise add the new one only if opted in.
      if (!isPickup && user) {
        if (selectedAddrId) updateAddress(selectedAddrId, addr);
        else if (saveAddr) addAddress(addr, saveLabel.trim() || addr.area);
      }

      // Remember this order on the device so guests can track it later.
      addRecentOrder({ id: result.orderId, total });

      if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
        return;
      }

      clear();
      navigate("/confirmation", {
        state: {
          orderId: result.orderId,
          name: name.trim(),
          paymentMethod: payment,
          subtotal,
          discount,
          deliveryFee,
          total,
          scheduledFor,
        },
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.startsWith("SOLD_OUT") ? t("common.soldOut") : t("checkout.required"));
      setSubmitting(false);
    }
  }

  const areaOptions = branch.serviceAreas.map((a) => (lang === "ar" ? a.ar : a.en));

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <h1 className="display text-2xl text-ink">{t("checkout.title")}</h1>

      {/* Delivery location — change branch/area/pin here; recalculates the fee. */}
      <section className="card p-4">
        <button
          onClick={() => setShowLocation((s) => !s)}
          className="flex w-full items-center justify-between text-start"
        >
          <span className="flex flex-wrap items-center gap-x-2 text-sm">
            <span>{isPickup ? "🏪" : "📍"}</span>
            <span className="text-black/50">
              {isPickup ? t("location.pickupFrom") : t("location.deliverTo")}:
            </span>
            <span className="font-semibold text-ink">
              {isPickup ? pick(branch.name, branch.nameAr) : locLabel}
            </span>
            {!isPickup && deliverable && (
              <span className="text-black/45">· {formatKWD(deliveryFee, lang)}</span>
            )}
          </span>
          <span className="shrink-0 text-sm font-semibold text-brand">
            {t("location.change")}
          </span>
        </button>
        {(showLocation || !deliverable) && (
          <div className="mt-3">
            <LocationSheet onDone={() => setShowLocation(false)} />
          </div>
        )}
        {q && !q.deliverable && (
          <p className="mt-2 text-sm font-semibold text-brand">{t("cart.outsideArea")}</p>
        )}
        {q?.belowMin && (
          <p className="mt-2 text-sm font-semibold text-amber-700">
            {t("cart.belowMin", {
              amount: formatKWD(q.amountToMin, lang),
              min: formatKWD(q.minOrder, lang),
            })}
          </p>
        )}
      </section>

      {/* Contact */}
      <section className="card space-y-3 p-4">
        <h2 className="font-bold text-ink">{t("checkout.contact")}</h2>
        <Field label={t("checkout.name")} required>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t("checkout.phone")} required>
          <input
            className="input"
            inputMode="tel"
            dir="ltr"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+965 …"
          />
        </Field>
      </section>

      {/* Address — delivery only (pickup needs no address) */}
      {!isPickup && (
      <section className="card space-y-3 p-4">
        <h2 className="font-bold text-ink">{t("checkout.addressTitle")}</h2>

        {/* Saved-address picker (members with a saved address book) */}
        {savedAddresses.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {savedAddresses.map((a) => {
              const on = selectedAddrId === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => {
                    setSelectedAddrId(a.id);
                    setSaveAddr(false);
                    setAddr({
                      governorate: a.governorate,
                      area: a.area,
                      block: a.block,
                      street: a.street,
                      building: a.building,
                      jadda: a.jadda,
                      floor: a.floor,
                      apartment: a.apartment,
                      paci: a.paci,
                      notes: a.notes,
                    });
                  }}
                  className={`rounded-xl border p-2.5 text-start text-sm transition ${
                    on
                      ? "border-brand bg-brand/5"
                      : "border-black/10 bg-white hover:border-brand/40"
                  }`}
                >
                  <div className="font-semibold text-ink">📍 {a.label}</div>
                  <div className="text-xs text-black/50">{addressSummary(a)}</div>
                </button>
              );
            })}
            <button
              onClick={() => {
                setSelectedAddrId(null);
                setAddr({ governorate: "", area: "", block: "", street: "", building: "" });
              }}
              className={`rounded-xl border border-dashed p-2.5 text-sm font-semibold transition ${
                selectedAddrId === null
                  ? "border-brand text-brand"
                  : "border-black/20 text-black/50 hover:border-brand/40"
              }`}
            >
              ＋ {t("checkout.newAddress")}
            </button>
          </div>
        )}

        <AddressFields
          value={addr}
          onChange={(patch) => setAddr((a) => ({ ...a, ...patch }))}
          areaOptions={areaOptions}
        />

        {/* Offer to save a freshly-typed address (signed-in members only) */}
        {user && selectedAddrId === null && (
          <div className="space-y-2 border-t border-black/5 pt-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-brand"
                checked={saveAddr}
                onChange={(e) => setSaveAddr(e.target.checked)}
              />
              <span className="font-medium text-ink">{t("checkout.saveAddress")}</span>
            </label>
            {saveAddr && (
              <input
                className="input"
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                placeholder={t("checkout.addressLabel")}
              />
            )}
          </div>
        )}
      </section>
      )}

      {/* When — ASAP or scheduled */}
      <ScheduleField value={scheduledFor} onChange={setScheduledFor} />

      {/* Payment */}
      <section className="card space-y-2 p-4">
        <h2 className="font-bold text-ink">{t("checkout.payment")}</h2>
        {PAYMENT_METHODS.map((m) => (
          <label
            key={m}
            className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${
              payment === m ? "border-brand bg-brand/5" : "border-black/10"
            }`}
          >
            <input
              type="radio"
              name="pay"
              checked={payment === m}
              onChange={() => setPayment(m)}
              className="accent-brand"
            />
            <span className="font-semibold text-ink">{t(`checkout.${m}`)}</span>
          </label>
        ))}
      </section>

      {/* Promo code */}
      <section className="card p-4">
        <PromoField />
      </section>

      {/* Summary + submit */}
      <section className="card space-y-2 p-4">
        <SummaryRow label={t("cart.subtotal")} value={formatKWD(subtotal, lang)} />
        {discount > 0 && (
          <SummaryRow
            label={t("cart.discount")}
            value={`− ${formatKWD(discount, lang)}`}
            discount
          />
        )}
        {isPickup ? (
          <SummaryRow
            label={t("location.pickup")}
            value={pick(branch.name, branch.nameAr)}
          />
        ) : (
          <SummaryRow label={t("cart.deliveryFee")} value={formatKWD(deliveryFee, lang)} />
        )}
        <SummaryRow label={t("cart.total")} value={formatKWD(total, lang)} bold />
        <SummaryRow
          label={isPickup ? t("checkout.pickupEta") : t("checkout.eta")}
          value={`${eta.fast ? "⚡ " : ""}~${eta.minutes} ${t("common.minutes")}`}
        />
        {eta.fast && (
          <p className="rounded-xl bg-brand/5 p-2.5 text-sm font-semibold text-brand">
            {t("checkout.fastEta")}
          </p>
        )}
        {error && <p className="text-sm font-semibold text-brand">{error}</p>}
        <button
          className="btn-primary mt-2 w-full"
          onClick={submit}
          disabled={submitting || !deliverable}
        >
          {submitting
            ? payment === "cod"
              ? t("checkout.placing")
              : t("checkout.paying")
            : t("checkout.placeOrder")}
        </button>
      </section>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  bold,
  discount,
}: {
  label: string;
  value: string;
  bold?: boolean;
  discount?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-bold text-ink" : "text-black/60"}>{label}</span>
      <span
        className={
          discount
            ? "font-semibold text-emerald-600"
            : bold
              ? "price text-lg"
              : "font-semibold"
        }
      >
        {value}
      </span>
    </div>
  );
}
